import { TextBadge } from '@carvana/ds-badge';
import { decorative, icon } from '@carvana/ds-styles';
import { Clickable } from '@carvana/merch-components';
import cx from 'classnames';
import { Heart } from 'lucide-react';
import { useContext } from 'react';
import SectionWrapper from '@/home/common/SectionWrapper';
import VehicleCarousel from '@/home/common/VehicleCarousel';
import { VehicleTileWrapper as TileWrapper } from '@/home/common/VehicleCarousel/VehicleTile/VehicleTile.styles';
import SortOption from '~/constants/common/SortOption';
import Sections from '~/constants/home/sections';
import RecentSearchesContext from '~/context/home/RecentSearchesContext';
import type { RecentSearch } from '~/contracts/home';
import useGetConsumerHref from '~/hooks/common/selectors/useGetConsumerHref';
import useToggleSavedSearches from '~/hooks/home/services/useToggleSavedSearches';
import { nativeSrpPayload } from '~/native/common/utils';
import getRelativeDate from '~/utilities/date/getRelativeDate';
import decodeBase64 from '~/utilities/decodeBase64';
import encodeBase64 from '~/utilities/encodeBase64';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import LoadingSavedSearchCarousel from './LoadingSavedSearchCarousel';
import {
  TileContainer,
  TileTopSectionContent,
  TileTopSectionWrapper
} from './RecentSearches.styles';

type RecentSearchTileProps = {
  tileInfo: RecentSearch;
};

export const addNewestInventorySortToCvnaId = (cvnaId: string): string => {
  const { filters } = JSON.parse(decodeBase64(cvnaId));

  return encodeBase64(
    JSON.stringify({
      filters,
      sortBy: SortOption.NewestInventory
    })
  );
};

function RecentSearchTile({ tileInfo }: RecentSearchTileProps) {
  const {
    cvnaId,
    savedSearch: { savedSearchId = 0, name = '' } = {},
    display: { name: displayName },
    totalMatchingVehicles = -1,
    totalNewVehicles,
    searchExecutedDateTime
  } = tileInfo;
  const getHref = useGetConsumerHref();
  const alertName = name || displayName;
  const toggleSavedSearch = useToggleSavedSearches({
    search: tileInfo
  });
  const isSavedSearch = Boolean(savedSearchId);

  return (
    <div className="relative h-full">
      <a
        href={getHref(
          `/cars/filters?cvnaid=${addNewestInventorySortToCvnaId(cvnaId)}`
        )}
        aria-label={alertName}
        data-analytics-id="Home feed card"
        data-analytics-attributes={JSON.stringify({
          description: 'Recent search tile',
          content: tileInfo.display
        })}
        data-native-payload={nativeSrpPayload}
        data-testid="recent-search-tile"
        className="group/tile block h-full"
      >
        <TileContainer>
          <TileTopSectionWrapper>
            <TileTopSectionContent>
              <div
                className="text-text-weak t-body-sm"
                title={searchExecutedDateTime}
              >
                {getRelativeDate(searchExecutedDateTime)}
              </div>
              <div
                className="line-clamp-2 text-text-strong t-label-md"
                data-testid="recent-searches-alert-name"
              >
                {alertName}
              </div>
            </TileTopSectionContent>
            <div className="h-32 w-32" aria-hidden="true" />
          </TileTopSectionWrapper>
          <div className="mt-auto flex items-center gap-md">
            {totalNewVehicles ? (
              <TextBadge
                variant="outline"
                priority="custom"
                customBorderColor={decorative['4Strong']}
              >
                {totalNewVehicles.toLocaleString()} New
              </TextBadge>
            ) : null}
            {totalMatchingVehicles >= 0 && (
              <div
                className="text-text-weak t-body-sm"
                data-testid="matched-results"
              >
                {totalMatchingVehicles.toLocaleString()} result
                {totalMatchingVehicles > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </TileContainer>
      </a>
      <Clickable
        onClick={toggleSavedSearch}
        data-analytics-id={null} // event handled in the toggle hook
        data-testid="saved search"
        aria-label={isSavedSearch ? 'Remove saved search' : 'Save search'}
        aria-pressed={isSavedSearch}
        className="absolute top-xl right-xl pointer-events-auto flex h-32 w-32 items-center justify-center"
      >
        <Heart
          aria-hidden="true"
          color={isSavedSearch ? icon.critical : icon.strong}
          className={cx(
            'hover:!stroke-icon-critical',
            isSavedSearch ? 'fill-icon-critical' : 'fill-icon-inverse'
          )}
        />
      </Clickable>
    </div>
  );
}

function RecentSearches() {
  const {
    recentSearches: { data: recentSearches, isLoading, isLoaded }
  } = useContext(RecentSearchesContext);

  if (isLoading) {
    return (
      <SectionWrapper analyticsSection="Recent Searches" hasBottomSpacing>
        <LoadingSavedSearchCarousel />
      </SectionWrapper>
    );
  }

  if (isLoaded && recentSearches.length === 0) return null;

  return (
    <SectionWrapper analyticsSection="Recent Searches" hasBottomSpacing>
      {recentSearches.length > 0 ? (
        <VehicleCarousel headerTitle="Recent searches">
          {recentSearches.map((recentSearch: RecentSearch) => {
            return (
              <TileWrapper key={recentSearch.display.name}>
                <RecentSearchTile tileInfo={recentSearch} />
              </TileWrapper>
            );
          })}
        </VehicleCarousel>
      ) : null}
    </SectionWrapper>
  );
}

export default withErrorBoundary(RecentSearches, {
  section: Sections.RecentSearches
});
